import { hasDisplayableCompany } from "@/components/GlobalSearchModal/utils";

import { CompanyEnrichPreviewPerson } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

/**
 * The suggestions the Track step may offer. A preview person whose matched and current experiences
 * resolve to no company is dropped everywhere else the same people-search endpoint is read (the
 * global-search tabs and their counts) — and this step is the strictest case of all: the picks
 * become a tracker list, so a person with no employer behind them can never produce a signal.
 * Unfiltered they arrived pre-ticked, under a title belonging to no employer ("Reservist" beside an
 * empty Company cell).
 */
export const trackablePeople = (
	people: CompanyEnrichPreviewPerson[]
): CompanyEnrichPreviewPerson[] => people.filter(hasDisplayableCompany);

export interface TrackSelectionInput {
	/** The rows the table holds after the search, in display order. */
	people: CompanyEnrichPreviewPerson[];
	/** What was ticked before it ran. */
	previousSelected: Set<number>;
	/** Everyone the user had already been offered — a row missing here is new to them. */
	previousSeen: Set<number>;
	/** Picks that are not rows (pasted profile URLs); they spend the cap too. */
	reserved: number;
	max: number;
}

/**
 * Which rows arrive ticked after a search.
 *
 * A search re-runs on edited criteria, not for a new user: someone they unticked stays unticked
 * when they come back, and someone they picked keeps their slot ahead of rows they have never seen.
 * Only genuinely new people are ticked on their behalf, and only while the cap has room left after
 * the pasted profile URLs have taken theirs.
 */
export const selectionAfterSearch = ({
	people,
	previousSelected,
	previousSeen,
	reserved,
	max,
}: TrackSelectionInput): Set<number> => {
	const kept = people.filter((person) => previousSelected.has(person.id));
	const fresh = people.filter((person) => !previousSeen.has(person.id));
	const room = Math.max(0, max - reserved);

	return new Set([...new Set([...kept, ...fresh].map((person) => person.id))].slice(0, room));
};
